import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError, CAUSALI_SCARICO } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowUpFromLine, Search, ScanBarcode } from "lucide-react";

export default function ScaricoPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const preId = sp.get("product");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    quantita: 1, causale: "Officina interna",
    commessa: "", cliente: "", macchina: "", matricola: "",
    tecnico: user.role === "meccanico" ? user.name : "", note: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (preId) (async () => {
      const { data } = await api.get(`/products/${preId}`);
      setSelected(data);
    })();
  }, [preId]);

  const search = async (q) => {
    if (!q || q.length < 1) { setResults([]); return; }
    const { data } = await api.get("/products", { params: { q } });
    setResults(data.slice(0, 8));
  };

  const pick = (p) => { setSelected(p); setResults([]); setQuery(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post("/movements", {
        product_id: selected.id, tipo: "scarico",
        quantita: parseInt(form.quantita),
        causale: form.causale, commessa: form.commessa, cliente: form.cliente,
        macchina: form.macchina, matricola: form.matricola, tecnico: form.tecnico, note: form.note,
      });
      toast.success(`Scarico registrato. Giacenza residua: ${data.giacenza_dopo}`);
      nav(`/prodotti/${selected.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-12 w-12 bg-[#FF9F0A]/10 border border-[#FF9F0A]/30 flex items-center justify-center">
          <ArrowUpFromLine className="text-[#FF9F0A]" size={22} />
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Operazione</div>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight">Scarico Merce</h1>
        </div>
      </div>

      {!selected ? (
        <div className="gm-card p-6">
          <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Cerca prodotto</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
            <input data-testid="scarico-search-input" autoFocus value={query}
              onChange={(e)=>{ setQuery(e.target.value); search(e.target.value); }}
              placeholder="Es. PUN-EPI-0001 o 3083340916"
              className="gm-input pl-11" />
          </div>
          <button onClick={()=>nav("/scanner?dest=scarico")} className="mt-3 inline-flex items-center gap-2 text-sm text-[#4CAF50] uppercase tracking-widest">
            <ScanBarcode size={16} /> scansiona barcode
          </button>
          {results.length > 0 && (
            <div className="mt-4 border border-zinc-800 divide-y divide-zinc-800/50">
              {results.map(p => (
                <button key={p.id} onClick={()=>pick(p)} className="w-full text-left p-3 hover:bg-zinc-900 flex items-center justify-between" data-testid="scarico-search-result">
                  <div>
                    <div className="font-mono-data text-xs text-[#4CAF50]">{p.codice_interno}</div>
                    <div className="text-sm font-semibold">{p.descrizione}</div>
                    <div className="text-xs text-zinc-500">{p.marca} • {p.posizione || "—"}</div>
                  </div>
                  <div className="font-mono-data text-white">{p.giacenza}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="gm-card p-6 space-y-5">
          <div className="border-l-4 border-[#4CAF50] pl-4">
            <div className="font-mono-data text-xs text-[#4CAF50]">{selected.codice_interno}</div>
            <div className="font-semibold text-white">{selected.descrizione}</div>
            <div className="text-xs text-zinc-500">Disponibili: <span className="font-mono-data text-white">{selected.giacenza}</span></div>
            <button type="button" onClick={()=>setSelected(null)} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white mt-1">cambia prodotto</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Quantità da scaricare *</label>
              <input data-testid="scarico-quantita" type="number" min="1" max={selected.giacenza} required value={form.quantita} onChange={e=>setForm(s=>({...s, quantita: e.target.value}))} className="gm-input font-mono-data text-2xl" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Causale *</label>
              <select required value={form.causale} onChange={e=>setForm(s=>({...s, causale: e.target.value}))} className="gm-input" data-testid="scarico-causale">
                {CAUSALI_SCARICO.map(c=> <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Commessa / Officina</label>
              <input value={form.commessa} onChange={e=>setForm(s=>({...s, commessa: e.target.value}))} className="gm-input" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Cliente</label>
              <input value={form.cliente} onChange={e=>setForm(s=>({...s, cliente: e.target.value}))} className="gm-input" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Macchina</label>
              <input value={form.macchina} onChange={e=>setForm(s=>({...s, macchina: e.target.value}))} className="gm-input" placeholder="es. Epiroc SB552" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Matricola macchina</label>
              <input value={form.matricola} onChange={e=>setForm(s=>({...s, matricola: e.target.value}))} className="gm-input font-mono-data" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Tecnico / Meccanico</label>
              <input value={form.tecnico} onChange={e=>setForm(s=>({...s, tecnico: e.target.value}))} className="gm-input" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Note</label>
              <textarea rows="2" value={form.note} onChange={e=>setForm(s=>({...s, note: e.target.value}))} className="gm-input py-2 h-auto resize-none" />
            </div>
          </div>

          <button type="submit" disabled={busy} data-testid="scarico-submit-button" className="gm-btn-primary w-full h-14 rounded-sm flex items-center justify-center gap-2">
            <ArrowUpFromLine size={20} />
            {busy ? "Salvataggio..." : `Conferma scarico di ${form.quantita} pz`}
          </button>
        </form>
      )}
    </div>
  );
}
